import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { IoClose, IoPencilOutline } from 'react-icons/io5';
import { collection, getDocs, query, where, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import Pagination from '../../../components/common/Pagination';
import { useAuth } from '../../../context/AuthContext';
import {
  createAdminAccount,
  deleteAdminAccount,
  updateAdminAccountProfile,
  updateAdminAccountRole,
} from '../../../services/adminAccountService';
import {
  createManagedMailbox,
  listManagedMailboxes,
  updateManagedMailbox,
} from '../../../services/adminMailService';
import { formatMobilePhone, isValidMobilePhone } from '../../../utils/mobilePhone';
import {
  companyMailIdOf,
  deriveCompanyEmail,
  isValidCompanyEmail,
  isValidLoginEmail,
  normalizeLoginEmail,
} from '../../../utils/teamEmail';

const PER_PAGE = 20;
const TEMP_PASSWORD = '123456';
const POSITION_OPTIONS = ['사원', '영업담당', '매니저', '팀장', '부장', '이사', '대표'];

const emptyForm = {
  name: '', email: '', companyEmail: '', dept: '', position: '', phone: '', role: 'staff',
};

const profileFormOf = (account) => ({
  name: account?.name === '-' ? '' : account?.name || '',
  email: account?.email === '-' ? '' : account?.email || '',
  companyEmail: account?.companyEmail || '',
  dept: account?.dept === '-' ? '' : account?.dept || '',
  position: account?.position === '-' ? '' : account?.position || '',
  phone: account?.phone === '-' ? '' : account?.phone || '',
});

const toDate = (ts) => {
  if (!ts) return null;
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch {
    return null;
  }
};

const tsOf = (raw) => {
  if (!raw) return 0;
  try {
    if (raw.toDate) return raw.toDate().getTime();
    if (raw.seconds) return raw.seconds * 1000;
    return new Date(raw).getTime() || 0;
  } catch {
    return 0;
  }
};

const AdminAccount = () => {
  const { userData } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [editForm, setEditForm] = useState(profileFormOf(null));
  const [profileSaving, setProfileSaving] = useState(false);
  const [memo, setMemo] = useState('');
  const [memoSaving, setMemoSaving] = useState(false);
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleConfirm, setRoleConfirm] = useState(null);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      // 복합 인덱스 회피: where만 사용, 정렬은 client-side
      const q = query(collection(db, 'users'), where('role', 'in', ['admin', 'staff']));
      const [snap, mailboxPayload] = await Promise.all([
        getDocs(q),
        listManagedMailboxes().catch(() => ({ mailboxes: [] })),
      ]);
      const mailboxes = Array.isArray(mailboxPayload?.mailboxes) ? mailboxPayload.mailboxes : [];
      const mailboxByOwner = new Map();
      mailboxes.forEach((mailbox) => {
        const ownerKeys = [mailbox.owner?.profileId, mailbox.owner?.uid].filter(Boolean);
        ownerKeys.forEach((ownerKey) => mailboxByOwner.set(String(ownerKey), mailbox));
      });
      const list = snap.docs.map((d) => {
        const data = d.data();
        const mailbox = mailboxByOwner.get(d.id) || null;
        const email = normalizeLoginEmail(data.email || '');
        const companyEmail = normalizeLoginEmail(data.companyEmail || mailbox?.address || (email ? deriveCompanyEmail(email) : ''));
        return {
          id: d.id,
          name: data.name || '-',
          email: email || '-',
          companyEmail,
          mailboxId: mailbox?.id || '',
          mailboxProtected: Boolean(mailbox?.protected),
          dept: data.department || data.dept || '-',
          position: data.position || '-',
          phone: formatMobilePhone(data.phone) || '-',
          role: data.role,
          status: data.status || 'active',
          adminMemo: data.adminMemo || '',
          _createdTs: tsOf(data.createdAt),
          createdAt: toDate(data.createdAt) || '-',
        };
      }).sort((a, b) => b._createdTs - a._createdTs);
      setStaff(list);
    } catch {
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); }, []);

  const totalPages = Math.max(1, Math.ceil(staff.length / PER_PAGE));
  const paged = useMemo(() => staff.slice((page - 1) * PER_PAGE, page * PER_PAGE), [staff, page]);
  useEffect(() => { setPage((current) => Math.min(current, totalPages)); }, [totalPages]);

  const openDetail = (s) => {
    setDetail(s);
    setEditForm(profileFormOf(s));
    setMemo(s.adminMemo || '');
  };

  const closeDetail = () => {
    setDetail(null);
    setEditForm(profileFormOf(null));
    setMemo('');
  };

  const handleCreate = async () => {
    const name = form.name.trim();
    const email = normalizeLoginEmail(form.email);
    const companyEmail = normalizeLoginEmail(form.companyEmail || deriveCompanyEmail(email));
    const phone = form.phone.trim();
    if (!name || !email || !phone) {
      alert('이름, 연락처, 로그인 이메일을 모두 입력해 주세요.');
      return;
    }
    if (!isValidLoginEmail(email)) {
      alert('로그인 이메일 형식이 올바르지 않습니다. 외부 이메일 또는 @ieum.in 이메일을 입력해 주세요.');
      return;
    }
    if (!isValidCompanyEmail(companyEmail)) {
      alert('회사 이메일은 3~32자의 영문 소문자·숫자·점·밑줄·하이픈으로 이루어진 아이디@ieum.in 형식으로 입력해 주세요.');
      return;
    }
    if (!isValidMobilePhone(phone)) {
      alert('연락처는 010으로 시작하는 11자리를 010-0000-0000 형식으로 입력해 주세요.');
      return;
    }

    setCreating(true);
    let account = null;
    try {
      account = await createAdminAccount({
        email,
        companyEmail,
        password: TEMP_PASSWORD,
        name,
        phone,
        role: form.role,
        department: form.dept.trim(),
        position: form.position.trim(),
      });
      const mailbox = await createManagedMailbox(companyMailIdOf(companyEmail), account.uid);
      const now = new Date();
      setStaff((previous) => [{
        id: account.uid,
        name,
        email,
        companyEmail,
        mailboxId: mailbox.id || '',
        mailboxProtected: false,
        dept: form.dept.trim() || '-',
        position: form.position.trim() || '-',
        phone: formatMobilePhone(phone),
        role: form.role,
        status: 'active',
        adminMemo: '',
        _createdTs: now.getTime(),
        createdAt: toDate(now),
      }, ...previous]);
      alert(`팀원 로그인 계정과 ${companyEmail} 회사 메일이 생성·연결되었습니다.\n로그인: ${email}${email !== companyEmail ? ` 또는 ${companyEmail}` : ''}\n임시 비밀번호: ${TEMP_PASSWORD}`);
      setForm(emptyForm);
      setShowCreate(false);
      setPage(1);
    } catch (error) {
      if (account?.uid) {
        await deleteAdminAccount(account.uid).catch(() => {});
      }
      alert(error.message || '팀원 계정 및 메일 연결에 실패했습니다.');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async () => {
    if (!detail || roleSaving) return;
    const currentEmail = String(userData?.email || '').trim().toLowerCase();
    const targetEmail = String(detail.email || '').trim().toLowerCase();
    if (targetEmail === 'dididia@naver.com') {
      alert('시스템 관리자의 권한은 변경할 수 없습니다.');
      return;
    }
    if (targetEmail === currentEmail) {
      alert('현재 로그인한 본인의 관리자 권한은 이 화면에서 변경할 수 없습니다.');
      return;
    }
    const nextRole = detail.role === 'admin' ? 'staff' : 'admin';
    setRoleConfirm({ account: detail, nextRole });
  };

  const handleProfileSave = async () => {
    if (!detail || profileSaving) return;
    const nextProfile = {
      name: editForm.name.trim(),
      email: normalizeLoginEmail(editForm.email),
      companyEmail: normalizeLoginEmail(editForm.companyEmail),
      phone: formatMobilePhone(editForm.phone),
      department: editForm.dept.trim(),
      position: editForm.position.trim(),
    };
    if (!nextProfile.name || !isValidLoginEmail(nextProfile.email) || !isValidCompanyEmail(nextProfile.companyEmail)) {
      alert('이름, 로그인 이메일, 회사 이메일을 올바르게 입력해 주세요.');
      return;
    }
    if (!isValidMobilePhone(nextProfile.phone)) {
      alert('연락처는 010으로 시작하는 11자리를 010-0000-0000 형식으로 입력해 주세요.');
      return;
    }
    const companyEmailChanged = nextProfile.companyEmail !== detail.companyEmail;
    if (companyEmailChanged && !detail.mailboxId) {
      alert('연결된 회사 메일 계정을 찾을 수 없어 이메일을 변경할 수 없습니다. 새로고침 후 다시 시도해 주세요.');
      return;
    }

    setProfileSaving(true);
    let mailboxUpdated = false;
    try {
      if (companyEmailChanged) {
        await updateManagedMailbox(detail.mailboxId, {
          mailId: companyMailIdOf(nextProfile.companyEmail),
          description: nextProfile.name,
        });
        mailboxUpdated = true;
      }
      await updateAdminAccountProfile(detail.id, nextProfile);
      const updated = {
        ...detail,
        name: nextProfile.name,
        email: nextProfile.email,
        companyEmail: nextProfile.companyEmail,
        phone: nextProfile.phone,
        dept: nextProfile.department || '-',
        position: nextProfile.position || '-',
      };
      setDetail(updated);
      setEditForm(profileFormOf(updated));
      setStaff((previous) => previous.map((item) => item.id === detail.id ? updated : item));
      alert('팀원 정보가 저장되었습니다.');
    } catch (error) {
      if (mailboxUpdated) {
        await updateManagedMailbox(detail.mailboxId, {
          mailId: companyMailIdOf(detail.companyEmail),
          description: detail.name,
        }).catch(() => {});
      }
      alert(error.message || '팀원 정보 저장에 실패했습니다.');
    } finally {
      setProfileSaving(false);
    }
  };

  const confirmRoleChange = async () => {
    if (!roleConfirm || roleSaving) return;
    const { account, nextRole } = roleConfirm;
    const nextLabel = nextRole === 'admin' ? '관리자' : '매니저';
    setRoleSaving(true);
    try {
      await updateAdminAccountRole(account.id, nextRole);
      const updated = { ...account, role: nextRole };
      setDetail(updated);
      setStaff((previous) => previous.map((item) => item.id === account.id ? { ...item, role: nextRole } : item));
      setRoleConfirm(null);
      alert(`${account.name} 계정 권한을 ${nextLabel}(으)로 변경했습니다.`);
    } catch (error) {
      alert(error.message || '권한 변경에 실패했습니다.');
    } finally { setRoleSaving(false); }
  };

  const handleMemoSave = async () => {
    if (!detail) return;
    setMemoSaving(true);
    try {
      await updateDoc(doc(db, 'users', detail.id), {
        adminMemo: memo,
        updatedAt: serverTimestamp(),
      });
      setStaff((prev) => prev.map((s) => s.id === detail.id ? { ...s, adminMemo: memo } : s));
      setDetail((previous) => previous ? { ...previous, adminMemo: memo } : previous);
      alert('메모가 저장되었습니다.');
    } catch (err) {
      alert('메모 저장 실패: ' + err.message);
    } finally {
      setMemoSaving(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>팀원 관리</Title>
        <HeaderActions>
          <TextBtn onClick={fetchStaff}>새로고침</TextBtn>
          <PrimaryBtn onClick={() => { setForm(emptyForm); setShowCreate(true); }}>팀원 추가</PrimaryBtn>
        </HeaderActions>
      </Header>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: 150 }}>팀원</Th>
              <Th style={{ minWidth: 230 }}>이메일</Th>
              <Th style={{ width: 130 }}>연락처</Th>
              <Th style={{ width: 120 }}>부서</Th>
              <Th style={{ width: 100 }}>직급</Th>
              <Th style={{ width: 100 }}>권한</Th>
              <Th style={{ width: 90 }}>상태</Th>
              <Th style={{ width: 110 }}>등록일</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><Td colSpan={8} style={{ textAlign: 'center', padding: '40px 0' }}>로딩 중...</Td></tr>
            ) : paged.length === 0 ? (
              <tr>
                <Td colSpan={8} style={{ padding: 0 }}>
                  <EmptyState>
                    <EmptyTitle>등록된 팀원이 없습니다</EmptyTitle>
                    <EmptyDesc>우측 상단 &quot;팀원 추가&quot; 버튼으로 계정을 만들 수 있습니다.</EmptyDesc>
                  </EmptyState>
                </Td>
              </tr>
            ) : paged.map((s) => (
              <tr key={s.id}>
                <Td data-label="팀원">
                  <AccountCellButton type="button" onClick={() => openDetail(s)}>
                    <strong>{s.name}</strong>
                    <AccountId>ID {companyMailIdOf(s.companyEmail || s.email)}</AccountId>
                  </AccountCellButton>
                </Td>
                <Td data-label="이메일">
                  <EmailCellButton type="button" onClick={() => openDetail(s)} aria-label={`${s.name} 이메일 및 정보 수정`}>
                    <EmailStack>
                      <span><EmailKind>로그인</EmailKind>{s.email}</span>
                      {s.companyEmail && s.companyEmail !== s.email && (
                        <span><EmailKind>회사</EmailKind>{s.companyEmail}</span>
                      )}
                    </EmailStack>
                    <IoPencilOutline size={15} aria-hidden="true" />
                  </EmailCellButton>
                </Td>
                <Td data-label="연락처">{s.phone}</Td>
                <Td data-label="부서">{s.dept}</Td>
                <Td data-label="직급">{s.position}</Td>
                <Td data-label="권한">{String(s.email || '').toLowerCase() === 'dididia@naver.com' ? '시스템 관리자' : s.role === 'admin' ? '관리자' : '매니저'}</Td>
                <Td data-label="상태">
                  <StatusText $on={s.status === 'active'}>
                    {s.status === 'active' ? '활성' : '비활성'}
                  </StatusText>
                </Td>
                <Td data-label="등록일" style={{ color: '#6B7280' }}>{s.createdAt}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pagination current={page} total={totalPages} onChange={setPage} />
      </TableWrap>

      {showCreate && (
        <Overlay onClick={() => setShowCreate(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>팀원 추가</ModalTitle>
              <CloseBtn onClick={() => setShowCreate(false)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <WarnBox>
                외부 이메일을 입력하면 같은 ID의 <b>@ieum.in</b> 회사 메일을 자동 제안합니다. 외부 이메일과 회사 이메일 모두 로그인에 사용할 수 있으며 임시 비밀번호는 <b>{TEMP_PASSWORD}</b>입니다.
              </WarnBox>
              <Row>
                <Field><FieldLabel>이름 *</FieldLabel><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="홍길동" /></Field>
                <Field>
                  <FieldLabel>로그인 이메일 *</FieldLabel>
                  <Input type="email" required value={form.email} onChange={(e) => {
                    const email = e.target.value.toLowerCase();
                    setForm({ ...form, email, companyEmail: email ? deriveCompanyEmail(email) : '' });
                  }} placeholder="name@naver.com 또는 name@ieum.in" />
                  <FieldHint>외부 이메일과 내부 이메일을 모두 입력할 수 있습니다.</FieldHint>
                </Field>
              </Row>
              <Row>
                <Field>
                  <FieldLabel>회사 이메일 *</FieldLabel>
                  <Input type="email" required value={form.companyEmail} onChange={(e) => setForm({ ...form, companyEmail: e.target.value.toLowerCase() })} placeholder="name@ieum.in" />
                  <FieldHint>외부 이메일 기준으로 자동 입력되며 중복 시 수정할 수 있습니다.</FieldHint>
                </Field>
                <Field><FieldLabel>연락처 *</FieldLabel><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: formatMobilePhone(e.target.value) })} inputMode="tel" maxLength={13} pattern="010-[0-9]{4}-[0-9]{4}" placeholder="010-0000-0000" title="010으로 시작하는 11자리 전화번호" /></Field>
              </Row>
              <Row>
                <Field><FieldLabel>부서</FieldLabel><Input value={form.dept} onChange={(e) => setForm({ ...form, dept: e.target.value })} placeholder="예: 운영팀" /></Field>
                <Field><FieldLabel>직급</FieldLabel><Select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}>
                  <option value="">선택</option>
                  {POSITION_OPTIONS.map((position) => <option key={position} value={position}>{position}</option>)}
                </Select></Field>
              </Row>
              <Row>
                <Field><FieldLabel>권한</FieldLabel>
                  <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    <option value="staff">매니저</option>
                    <option value="admin">관리자</option>
                  </Select>
                </Field>
                <Field aria-hidden="true" />
              </Row>
            </ModalBody>
            <ModalFooter>
              <TextBtn onClick={() => setShowCreate(false)} disabled={creating}>취소</TextBtn>
              <PrimaryBtn onClick={handleCreate} disabled={creating}>
                {creating ? '등록 중...' : '등록'}
              </PrimaryBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}

      {detail && (
        <Overlay onClick={closeDetail}>
          <WideModal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>팀원 정보 수정</ModalTitle>
              <CloseBtn onClick={closeDetail}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <EditGrid>
                <Field><FieldLabel>이름 *</FieldLabel><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></Field>
                <Field>
                  <FieldLabel>로그인 이메일 *</FieldLabel>
                  <Input type="email" value={editForm.email} disabled={detail.mailboxProtected || normalizeLoginEmail(detail.email) === 'dididia@naver.com'} onChange={(e) => setEditForm({ ...editForm, email: e.target.value.toLowerCase() })} />
                  <FieldHint>외부 이메일 또는 회사 이메일로 로그인합니다.</FieldHint>
                </Field>
                <Field>
                  <FieldLabel>회사 이메일 *</FieldLabel>
                  <Input type="email" value={editForm.companyEmail} disabled={detail.mailboxProtected || normalizeLoginEmail(detail.email) === 'dididia@naver.com'} onChange={(e) => setEditForm({ ...editForm, companyEmail: e.target.value.toLowerCase() })} />
                  <FieldHint>외부 이메일 계정도 이 주소로 로그인할 수 있습니다.</FieldHint>
                </Field>
                <Field><FieldLabel>연락처 *</FieldLabel><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: formatMobilePhone(e.target.value) })} inputMode="tel" maxLength={13} /></Field>
                <Field><FieldLabel>부서</FieldLabel><Input value={editForm.dept} onChange={(e) => setEditForm({ ...editForm, dept: e.target.value })} /></Field>
                <Field><FieldLabel>직급</FieldLabel><Select value={editForm.position} onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}>
                  <option value="">선택</option>
                  {POSITION_OPTIONS.map((position) => <option key={position} value={position}>{position}</option>)}
                </Select></Field>
              </EditGrid>

              <AccountMeta>
                <DetailRow><DetailLabel>권한</DetailLabel><DetailValue>{normalizeLoginEmail(detail.email) === 'dididia@naver.com' ? '시스템 관리자' : detail.role === 'admin' ? '관리자' : '매니저'}</DetailValue>
                  {normalizeLoginEmail(detail.email) !== 'dididia@naver.com' && (
                    <SmallTextBtn type="button" disabled={roleSaving || normalizeLoginEmail(detail.email) === normalizeLoginEmail(userData?.email)} onClick={handleRoleChange}>
                      {roleSaving ? '변경 중...' : detail.role === 'admin' ? '매니저로 변경' : '관리자로 변경'}
                    </SmallTextBtn>
                  )}
                </DetailRow>
                <DetailRow><DetailLabel>상태</DetailLabel><DetailValue>{detail.status === 'active' ? '활성' : '비활성'}</DetailValue></DetailRow>
                <DetailRow><DetailLabel>등록일</DetailLabel><DetailValue>{detail.createdAt}</DetailValue></DetailRow>
              </AccountMeta>

              <DetailCard>
                <DetailCardHeader>
                  <DetailCardTitle>관리자 메모</DetailCardTitle>
                  <SmallTextBtn disabled={memoSaving} onClick={handleMemoSave}>
                    {memoSaving ? '저장 중...' : '메모 저장'}
                  </SmallTextBtn>
                </DetailCardHeader>
                <MemoArea value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="담당자 메모를 남길 수 있습니다." />
              </DetailCard>
            </ModalBody>
            <ModalFooter>
              <TextBtn type="button" disabled={profileSaving} onClick={closeDetail}>취소</TextBtn>
              <PrimaryBtn type="button" disabled={profileSaving} onClick={handleProfileSave}>{profileSaving ? '저장 중...' : '정보 저장'}</PrimaryBtn>
            </ModalFooter>
          </WideModal>
        </Overlay>
      )}

      {roleConfirm && (
        <Overlay onClick={() => !roleSaving && setRoleConfirm(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>계정 권한을 변경하시겠습니까?</ModalTitle>
              <CloseBtn disabled={roleSaving} onClick={() => setRoleConfirm(null)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <WarnBox>
                <b>{roleConfirm.account.email}</b> 계정을 {roleConfirm.nextRole === 'admin' ? '관리자' : '매니저'} 권한으로 변경합니다. 관리자 권한 회수 시 기존 관리자 화면 접근이 즉시 제한됩니다.
              </WarnBox>
            </ModalBody>
            <ModalFooter>
              <TextBtn type="button" disabled={roleSaving} onClick={() => setRoleConfirm(null)}>취소</TextBtn>
              <PrimaryBtn type="button" disabled={roleSaving} onClick={confirmRoleChange}>{roleSaving ? '변경 중...' : '확인 후 변경'}</PrimaryBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}

    </Container>
  );
};

export default AdminAccount;

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const TextBtn = styled.button`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: none;
  padding: 4px 0;
  &:hover { color: ${({ theme }) => theme.colors.primary}; text-decoration: underline; }
`;

const SmallTextBtn = styled.button`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary};
  background: none;
  padding: 2px 0;
  &:hover:not(:disabled) { text-decoration: underline; }
  &:disabled { color: ${({ theme }) => theme.colors.textLight}; cursor: default; }
`;

const PrimaryBtn = styled.button`
  padding: 8px 18px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  font-weight: 500;
`;

const TableWrap = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  @media (max-width: 768px) {
    display: block;
    thead { display: none; }
    tbody { display: grid; gap: 12px; padding: 12px; }
    tr {
      display: block;
      border: 1px solid ${({ theme }) => theme.colors.border};
      border-radius: ${({ theme }) => theme.radius.sm};
      overflow: hidden;
    }
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 13px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  vertical-align: middle;
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 82px minmax(0, 1fr);
    gap: 10px;
    padding: 10px 12px;
    &:last-child { border-bottom: 0; }
    &::before {
      content: attr(data-label);
      color: ${({ theme }) => theme.colors.textSecondary};
      font-size: 12px;
      font-weight: 500;
    }
    &:not([data-label]) { display: block; }
    &:not([data-label])::before { display: none; }
  }
`;

const AccountCellButton = styled.button`
  background: none;
  padding: 0;
  display: grid;
  gap: 3px;
  text-align: left;
  color: ${({ theme }) => theme.colors.text};
  &:hover strong { color: ${({ theme }) => theme.colors.primary}; text-decoration: underline; }
`;

const AccountId = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const EmailCellButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0;
  background: none;
  color: ${({ theme }) => theme.colors.text};
  text-align: left;
  svg { flex: 0 0 auto; color: ${({ theme }) => theme.colors.textLight}; }
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const EmailStack = styled.span`
  display: grid;
  gap: 4px;
  min-width: 0;
  span { overflow-wrap: anywhere; }
`;

const EmailKind = styled.span`
  display: inline-block;
  min-width: 38px;
  margin-right: 6px;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 11px;
`;

const StatusText = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $on, theme }) => ($on ? theme.colors.success : theme.colors.textLight)};
`;

const EmptyState = styled.div`
  padding: 60px 0;
  text-align: center;
`;

const EmptyTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 6px;
`;

const EmptyDesc = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const WideModal = styled(Modal)`
  max-width: 760px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
`;

const CloseBtn = styled.button`
  color: ${({ theme }) => theme.colors.textLight};
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  align-items: center;
  padding: 14px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const WarnBox = styled.div`
  padding: 10px 14px;
  margin-bottom: 16px;
  background: #FEF3C7;
  border: 1px solid #FCD34D;
  color: #92400E;
  font-size: 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  line-height: 1.5;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 4px;
  @media (max-width: 640px) { display: block; }
`;

const Field = styled.div`
  flex: 1;
  margin-bottom: 16px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const FieldHint = styled.p`
  margin-top: 5px;
  font-size: 11px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textLight};
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
  &:disabled { background: ${({ theme }) => theme.colors.sidebarHover}; color: ${({ theme }) => theme.colors.textLight}; }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  background: #fff;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const EditGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const AccountMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.sidebarHover};
  @media (max-width: 640px) { grid-template-columns: 1fr; gap: 4px; }
`;

const DetailCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 16px;
`;

const DetailCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const DetailCardTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 12px;
  ${DetailCardHeader} & {
    margin-bottom: 0;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 12px;
`;

const DetailLabel = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DetailValue = styled.span`
  color: ${({ theme }) => theme.colors.text};
`;

const MemoArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  font-family: inherit;
  resize: vertical;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;
